<template>
  <div class="theme-switcher">
    <button v-show="visible" @click="toggleTheme" v-if="!$util.isAdmin()" class="theme-switcher-btn" :title="isDark ? 'Alterar para tema claro' : 'Alterar para tema escuro'">
      <span class="theme-switcher-icon" v-if="isDark">
        <b-icon-sun/>
      </span>
      <span class="theme-switcher-icon" v-else>
        <b-icon-moon/>
      </span>
      <span class="theme-switcher-label">{{ isDark ? 'Tema claro' : 'Tema escuro' }}</span>
    </button>
  </div>
</template>

<script>
export default {
  props: {
    // Controla a visibilidade do botão. Mantém o comportamento original
    // (invisível) onde já era usado (Header/HeaderMobile), e passa a
    // aparecer explicitamente onde for solicitado (ex.: página de Login).
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // Padrão: tema escuro (usado quando não há preferência salva)
      isDark: true, 
    };
  },
  created(){
    // Restaura a preferência salva. Antes, só existia um caminho para
    // isDark=true (nunca para false), então o tema claro nunca
    // sobrevivia ao reload feito por toggleTheme() - voltava sempre
    // para o escuro. Agora uma única chave ('siteTheme') guarda o
    // tema efetivamente escolhido ('dark' ou 'light').
    const previewTheme = new URLSearchParams(window.location.search).get('mx3_preview_theme');
    const savedTheme = localStorage.getItem('siteTheme');
    const effectiveTheme = (previewTheme === 'dark' || previewTheme === 'light') ? previewTheme : savedTheme;
    if (effectiveTheme === 'light') {
      this.isDark = false;
    } else if (effectiveTheme === 'dark') {
      this.isDark = true;
    }
    // Sem preferência salva: mantém o padrão (escuro).

    // Mantém múltiplas instâncias do botão (ex.: header + menu da conta)
    // sincronizadas entre si, já que cada uma tem seu próprio `isDark`
    // local - sem isso, alternar o tema em uma delas deixaria a outra
    // mostrando o ícone/rótulo desatualizado até um reload manual.
    this.$root.$on('mx3-theme-changed', this.onExternalThemeChange);
  },
  beforeDestroy() {
    this.$root.$off('mx3-theme-changed', this.onExternalThemeChange);
  },
  methods: {
    toggleTheme() {
      // Alterna o valor de `isDark`
      this.isDark = !this.isDark;

      // Salva a preferência explicitamente escolhida
      localStorage.setItem('siteTheme', this.isDark ? 'dark' : 'light');

      // Aplica o tema trocando apenas a folha de estilo (sem recarregar
      // a página inteira) e avisa as demais instâncias do botão.
      this.applyTheme();
      this.$root.$emit('mx3-theme-changed', this.isDark);
    },
    onExternalThemeChange(isDark) {
      if (this.isDark === isDark) return;
      this.isDark = isDark;
      this.applyTheme();
    },
    applyTheme() {
      // Remove qualquer link de tema existente para evitar duplicatas
      const oldLink = document.getElementById('theme-link');
      if (oldLink) {
        document.head.removeChild(oldLink);
      }

      // Cria um novo elemento <link>
      const link = document.createElement('link');
      link.id = 'theme-link';
      link.rel = 'stylesheet';
      link.type = 'text/css';

      // 4. Define o caminho do arquivo CSS com base no estado `isDark`:
      // isDark: true -> tema ESCURO ('main.css')
      // isDark: false -> tema CLARO ('main-light.css')
      //
      // "?v=" (cache-busting): main.css/main-light.css sempre tem o MESMO
      // nome de arquivo - o "Publicar" da tela Personalizar so reescreve o
      // conteudo. Sem um carimbo de versao na URL, o navegador (e qualquer
      // cache do servidor/CDN) pode continuar servindo a copia antiga por
      // bastante tempo, mesmo apos uma publicacao - obrigando a limpar o
      // cache do dominio manualmente para ver a mudanca. window.MX3_THEME_
      // VERSION (ver theme-version.js) e atualizado a cada publicacao;
      // anexando o valor aqui, cada publicacao aponta para uma URL
      // diferente e o navegador sempre busca a versao mais recente.
      const versoes = window.MX3_THEME_VERSION || {};
      const arquivo = this.isDark ? 'main.css' : 'main-light.css';
      const versao = versoes[this.isDark ? 'dark' : 'light'];
      link.href = '/css/' + arquivo + (versao ? ('?v=' + encodeURIComponent(versao)) : '');

      // Adiciona o novo link ao <head> do documento
      document.head.appendChild(link);
    },
  },
  mounted() {
    // Aplica o tema inicial quando o componente for montado
    this.applyTheme();
  },
};
</script>

<style scoped>
.theme-switcher-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background: color-mix(in srgb, var(--mx3-style-bg-card, var(--surface-color, #fff)), transparent 15%);
  border: 1px solid var(--mx3-style-border, color-mix(in srgb, var(--default-color, #444), transparent 75%));
  color: var(--mx3-style-text-primary, var(--default-color, #444));
  border-radius: 999px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  transition: 0.25s;
}
.theme-switcher-btn:hover {
  border-color: var(--mx3-style-primary, var(--accent-color, #24527a));
  color: var(--mx3-style-primary, var(--accent-color, #24527a));
}
.theme-switcher-icon {
  display: flex;
  align-items: center;
  font-size: 16px;
}
.theme-switcher-label {
  font-weight: 500;
}
</style>
